'use client';

import { useMemo, useRef } from 'react';
import Link from 'next/link';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, SearchIcon, UserCircle2Icon } from 'lucide-react';
import Highlight from 'react-highlight-words';

import { SearchCustomersProvider, useSearchCustomersCtx } from '@/app/(app)/customers-home/provider';
import Logo from '@/components/logo';
import { useRecentlyVisited } from '@/components/providers/recently-visited-provider';
import { useApiCustomers, useApiCustomersByIds, useCustomer } from '@/components/providers/v1-cutsomer-provider';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

const MAX_DISPLAY_ITEMS = 10;
// Search limit should be higher than the max display items
// And ideally match the limit used for query `/customers` table
// so when user clicks on "View all results", they will see all results instantly
const SEARCH_LIMIT = 20;

export default function CustomersHomePage() {
  return (
    <SearchCustomersProvider>
      <Content />
    </SearchCustomersProvider>
  );
}

function Content() {
  const { search } = useSearchCustomersCtx();

  return (
    <div className='flex w-full flex-col items-center py-10'>
      <div className='flex w-full max-w-2xl flex-col items-center'>
        <Title />
        <div className='h-6' />
        <SearchBar />
        <div className='h-8' />
        {search.length > 0 ? <CustomersSearchResults /> : <CustomersRecentlyVisited />}
      </div>
    </div>
  );
}

function Title() {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  return (
    <motion.h2
      className='text-heading-2xl font-normal'
      initial={{ filter: 'blur(10px)', opacity: 0 }}
      animate={isInView ? { filter: 'blur(0px)', opacity: 1 } : {}}
      transition={{ duration: 0.5 }}
      ref={ref}
    >
      {greeting}
    </motion.h2>
  );
}

function SearchBar() {
  const { search, setSearch } = useSearchCustomersCtx();
  return (
    <label
      htmlFor='search'
      className='flex w-full items-center gap-4 rounded-3xl px-5 py-[17px] shadow-sm dark:border-[0.5px]'
    >
      <SearchIcon className='size-5' />
      <input
        autoFocus
        id='search'
        type='text'
        placeholder='Search for a customer'
        className='w-full outline-none'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </label>
  );
}

function CustomersRecentlyVisited() {
  const items = useRecentlyVisited();
  const targetCustomerIds = useMemo(
    () =>
      items
        .filter((item) => item.entity === 'customer')
        .map((item) => item.id)
        .slice(0, MAX_DISPLAY_ITEMS),
    [items]
  );
  const { data, isLoading } = useApiCustomersByIds(targetCustomerIds);

  // Sort API results to match the original visit order (most recent first)
  const sortedCustomerIds = useMemo(() => {
    if (!data) return [];
    const fetchedIds = new Set(data.map((c) => c.id));
    return targetCustomerIds.filter((id) => fetchedIds.has(id));
  }, [data, targetCustomerIds]);

  if (isLoading) {
    if (targetCustomerIds.length === 0) {
      return null;
    }
    return (
      <div className='flex w-full flex-col gap-3'>
        <div className='text-sm text-muted-foreground'>Recently visited</div>
        {Array.from({ length: targetCustomerIds.length }).map((_, index) => (
          <Skeleton key={index} className='h-20 w-full' />
        ))}
      </div>
    );
  }

  if (sortedCustomerIds.length === 0) {
    return (
      <div className='flex w-full flex-col items-center justify-center gap-1 rounded-2xl py-20 shadow-sm'>
        <Logo className='size-8' />
        <h2 className='mt-2'>Welcome to Solea</h2>
        <p className='text-sm text-muted-foreground'>Search for a customer to get started</p>
      </div>
    );
  }

  return (
    <div className='flex w-full flex-col gap-3'>
      <div className='text-sm text-muted-foreground'>Recently visited</div>
      {sortedCustomerIds.map((id) => (
        <CustomerListItem key={id} id={id} />
      ))}
    </div>
  );
}

function CustomersSearchResults() {
  const { search } = useSearchCustomersCtx();
  const debouncedSearch = useDebouncedValue(search, 200);
  const { data, isFetching } = useApiCustomers({
    query: debouncedSearch,
    limit: SEARCH_LIMIT,
    page: 1,
  });

  if (isFetching) {
    return (
      <div className='flex w-full flex-col gap-3'>
        <div className='text-sm text-muted-foreground'>Search results</div>
        {Array.from({ length: MAX_DISPLAY_ITEMS }).map((_, index) => (
          <Skeleton key={index} className='h-20 w-full' />
        ))}
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className='flex w-full items-center justify-center rounded-2xl border border-dashed border-muted-foreground/20 py-20'>
        <p className='text-sm text-muted-foreground'>No customers match your search</p>
      </div>
    );
  }
  return (
    <div className='flex w-full flex-col gap-3'>
      <div className='text-sm text-muted-foreground'>Search results</div>
      {data.items.slice(0, MAX_DISPLAY_ITEMS).map((customer) => (
        <CustomerListItem key={customer.id} id={customer.id} />
      ))}
      {data.items.length > MAX_DISPLAY_ITEMS && (
        <Button variant='ghost' size='sm' className='ml-auto text-muted-foreground' asChild>
          <Link href={`/customers?query=${debouncedSearch}`}>
            View all results
            <ArrowRightIcon className='size-4' />
          </Link>
        </Button>
      )}
    </div>
  );
}

function CustomerListItem({ id }: { id: string }) {
  const customer = useCustomer(id);
  const hasPhoneOrEmail = customer.formatted.primaryPhone || customer.formatted.primaryEmail;
  const hasPhoneAndEmail = customer.formatted.primaryPhone && customer.formatted.primaryEmail;
  return (
    <Link
      href={`/customers/${id}`}
      className='flex items-center gap-4 rounded-2xl px-4 py-3 shadow-sm transition-colors hover:bg-accent/50 dark:border-[0.5px]'
    >
      <UserCircle2Icon className='size-5' />
      <div className='flex flex-col gap-1.5'>
        <div className='inline-flex items-center gap-1.5 text-sm leading-none'>
          <span>
            <SearchHighlight text={customer.formatted.mainName ?? 'No name'} />
          </span>
          {customer.formatted.secondaryName && (
            <div className='text-sm text-muted-foreground'>
              - <SearchHighlight text={customer.formatted.secondaryName} />
            </div>
          )}
          {customer.formatted.externalId && (
            <span className='text-xs font-medium text-muted-foreground'>
              #
              <span>
                <SearchHighlight text={customer.formatted.externalId} />
              </span>
            </span>
          )}
          {customer.status === 'INACTIVE' && (
            <Tooltip>
              <TooltipTrigger>
                <div className='size-2 rounded-full bg-destructive' />
              </TooltipTrigger>
              <TooltipContent>
                <p>This customer is inactive</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {customer.formatted.address && (
          <div className='text-sm leading-none text-muted-foreground'>
            <SearchHighlight text={customer.formatted.address} />
          </div>
        )}
        {hasPhoneOrEmail && (
          <div className='flex items-center gap-2 text-sm leading-none text-muted-foreground'>
            {customer.formatted.primaryPhone && (
              <div>
                <SearchHighlight text={customer.formatted.primaryPhone} />
              </div>
            )}
            {hasPhoneAndEmail && <div>•</div>}
            {customer.formatted.primaryEmail && (
              <div>
                <SearchHighlight text={customer.formatted.primaryEmail} />
              </div>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}

function SearchHighlight({ text }: { text: string | null }) {
  const { searchWords } = useSearchCustomersCtx();
  if (!text) {
    return null;
  }
  return (
    <Highlight
      searchWords={searchWords}
      textToHighlight={text}
      autoEscape
      highlightClassName='bg-amber-500/10 text-amber-500'
    />
  );
}
